import { useCallback } from 'react';

import { useQueryClient } from '@eika-infrastruktur/react-query';
import { formatCurrencyValue } from '@eika/currency-input';

import { useConfig } from '../../../../defaultConfig';
import { ensureAccountsData, useAccounts } from '../../../../fetchHooks/useAccount';
import usePaymentLimits, { ensurePaymentLimitsData } from '../../../../fetchHooks/usePaymentLimits';
import type { Account, PaymentLimits } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../../services/accountFilters';
import TranslationsConfigType from '../../../../translations/translationType';
import {
    doesAmountExceedAccountBalanceAccountBalance,
    doesAmountExceedPaymentLimit,
    formatLimitAmountError,
} from '../../../../utils/validation';
import { isNotToday } from '../../../../utils/validation/date';
import { UseValidateHook } from '../../../../utils/validation/validateTypes';
import { CommonPaymentState } from '../../form/CommonPaymentState';
import { invalidDate } from '../../validate';
import { EfakturaState } from './state';

export interface EfakturaError {
    accountId?: string;
    amount?: string;
    toAccount?: string;
    paymentDate?: string;
    amountLimit?: string;
}

export const isNoCoverage = (state: CommonPaymentState, targetFromAccount: Account | undefined): boolean => {
    const { fromAccount, amount, paymentDate } = state;
    if (!fromAccount || !targetFromAccount) return false;
    if (!amount) return false;
    if (isNotToday(paymentDate)) return false;

    return doesAmountExceedAccountBalanceAccountBalance(targetFromAccount, amount);
};

export const amountLimitWarning = (
    translated: TranslationsConfigType,
    state: EfakturaState,
    paymentLimits: PaymentLimits | undefined,
    targetFromAccount: Account | undefined,
): string | undefined => {
    const { amount, paymentDate, futurePaymentAmount } = state;
    const shouldValidateAgainsPaymentLimit =
        targetFromAccount?.owners.find((x) => x.primaryOwner)?.equalToUserLoggedOn ?? true;
    if (!shouldValidateAgainsPaymentLimit) return undefined;

    const paymentLimitError = doesAmountExceedPaymentLimit(amount, paymentDate, paymentLimits, futurePaymentAmount);
    return formatLimitAmountError(translated, paymentLimitError, paymentLimits) ?? undefined;
};

export const validate =
    (translated: TranslationsConfigType, paymentLimits: PaymentLimits | undefined, accounts: Account[]) =>
    (formState: EfakturaState): EfakturaError => {
        const errors: EfakturaError = {
            accountId: undefined,
            amount: undefined,
            toAccount: undefined,
            paymentDate: undefined,
            amountLimit: undefined,
        };

        const { fromAccount, toAccount, amount, paymentDate } = formState;

        const targetFromAccount = accounts
            .filter(isValidAccountPaymentFrom)
            .find((account) => account.accountNumber.value === fromAccount?.value);

        if (!fromAccount || !targetFromAccount?.accountId || targetFromAccount?.accountId.value === '') {
            errors.accountId = translated.paymentValidation.chooseFromAccount;
        }

        if (!toAccount) {
            errors.toAccount = translated.paymentValidation.chooseToAccount;
        }

        if (!amount || amount <= 0) {
            errors.amount = translated.paymentValidation.chooseAmount;
        } else if (isNoCoverage(formState, targetFromAccount)) {
            errors.amount = translated.paymentValidation.noCoverage;
        } else if (formState.minimumAmount && amount < formState.minimumAmount) {
            errors.amount = format(
                translated.paymentValidation.amountLowerThanMinimum,
                formatCurrencyValue(formState.minimumAmount),
            );
        } else {
            errors.amount = amountLimitWarning(translated, formState, paymentLimits, targetFromAccount);
        }

        if (invalidDate(paymentDate)) {
            errors.paymentDate = translated.paymentValidation.chooseValidDate;
        }

        return errors;
    };

const useValidate: UseValidateHook<EfakturaState, EfakturaError> = () => {
    const queryClient = useQueryClient();
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const paymentLimits = usePaymentLimits();

    const { data: accounts } = useAccounts();

    return useCallback(
        async (state) => {
            const currentPaymentLimits = await ensurePaymentLimitsData(queryClient, featureToggles.enablePaymentLimits);
            const currentAccounts = await ensureAccountsData(queryClient);
            return validate(translated, paymentLimits.data ?? currentPaymentLimits, accounts ?? currentAccounts)(state);
        },
        [queryClient, paymentLimits, translated, accounts, featureToggles.enablePaymentLimits],
    );
};

export default useValidate;
